/**
 * One destination row on the rail. `SortableRailRow` wraps it for drag,
 * spreading only dnd-kit's pointer `listeners` — not `attributes`, which exist
 * for the keyboard sensor we don't use and would put `role="button"` +
 * `tabIndex` on a wrapper around a link.
 */
import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import SideNavItem from './SideNavItem';
import { countForStar } from '../MobileChrome/starSlots';

import type { RailNodeData } from './useRailDrag';
import type { NavCounts } from '@app/stores/slices/uiSlice';
import type { ContextMenuItem } from '@features/workspace';
import type { Destination } from '@shared/navigation/destinations';

interface RailRowProps {
  destination: Destination;
  isCollapsed: boolean;
  counts: NavCounts;
  rowClick: { onClick?: () => void };
  contextMenuItems?: ContextMenuItem[];
  /** Keep the right-edge chevron column clear, so counts align with parents' counts. */
  rightInset?: boolean;
  /** Destination ids this row also lights for (a parent's hidden sub-items). */
  activeFor?: string[];
}

/** A destination as a nav row. */
export const RailRow: React.FC<RailRowProps> = ({
  destination: d,
  isCollapsed,
  counts,
  rowClick,
  contextMenuItems,
  rightInset,
  activeFor,
}) => (
  <SideNavItem
    id={d.id}
    label={d.label}
    icon={d.icon}
    {...(d.domain && { domain: d.domain })}
    isCollapsed={isCollapsed}
    count={countForStar(d.id, counts)}
    path={d.path}
    {...(contextMenuItems && { contextMenuItems })}
    {...(rightInset && { rightInset })}
    {...(activeFor && { activeFor })}
    {...rowClick}
  />
);

/**
 * Drag wrapper around one row. `container` is where the row sits (the top level,
 * a section id, or a parent row id). `below` renders inside the dragged block but
 * outside the drag handle — a parent's sub-items travel with it, yet dragging a
 * sub-item starts from the sub-item.
 */
export const SortableRailRow: React.FC<{
  id: string;
  container: string;
  children: React.ReactNode;
  below?: React.ReactNode;
}> = ({ id, container, children, below }) => {
  const data: RailNodeData = { kind: 'row', container };
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid="sortable-star-row">
      <div {...listeners}>{children}</div>
      {below}
    </div>
  );
};
